/* ============================================================
   ManMade — PDP hero gallery + mini review carousel
   window.mmHeroInit() binds the main gallery (thumbs, arrows, dots,
   drag-follow swipe) and the buybox mini-review carousel.

   Lives in an asset, not the hero's {% javascript %} block, because
   Shopify only runs a section's {% javascript %} on pages that render
   that section. home-rvt-hero fetches the RVT PDP hero into the home
   page, where pdp-hero is never "rendered", so the compiled script was
   skipped and the home gallery was dead (no swipe, arrows, dots or
   thumbs). Loaded by pdp-hero.liquid (so it also rides along in the
   Section Rendering API HTML that home-rvt-hero injects). Re-runnable and
   idempotent: each widget binds once, so a second call is a no-op.
   ============================================================ */
// Re-runnable: home-rvt-hero injects this hero into the home page and calls window.mmHeroInit() again.
window.mmHeroInit = function() {
  // ── Gallery ──────────────────────────────────────────────────────────
  const gallery = document.getElementById('mm-main-gallery');
  // Pristine markup, kept as a string (no image fetches) so mmGallerySet can rebuild/restore it.
  const galRoot = document.getElementById('mm-gallery');
  if (galRoot && !window.__mmGalHTML && gallery && !gallery.__mmGalleryBound) window.__mmGalHTML = galRoot.outerHTML;
  // Bind once per gallery node: two binds would each keep their own `current`,
  // so a tap would move the strip twice and the copies would drift apart.
  if (gallery && !gallery.__mmGalleryBound) {
    gallery.__mmGalleryBound = true;
    const track   = gallery.querySelector('[data-mm-gallery-track]');
    const slides  = track ? Array.from(track.children) : [];
    const section = gallery.closest('.mm-hero');
    const thumbs  = section ? Array.from(section.querySelectorAll('.mm-hero__thumb')) : [];
    const prevBtn = gallery.querySelector('[data-mm-gallery-prev]');
    const nextBtn = gallery.querySelector('[data-mm-gallery-next]');
    const galleryDotsWrap = gallery.querySelector('[data-mm-gallery-dots]');

    let current = 0;
    const last = slides.length - 1;

    let galleryDots = [];
    if (galleryDotsWrap && slides.length > 1) {
      slides.forEach(function(_, i) {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'mm-hero__dot' + (i === 0 ? ' is-active' : '');
        d.setAttribute('aria-label', 'Go to image ' + (i + 1) + ' of ' + slides.length);
        d.addEventListener('click', function() { goTo(i); });
        galleryDotsWrap.appendChild(d);
      });
      galleryDots = Array.from(galleryDotsWrap.children);
    }
    // Story bars + "1 / N" counter (mobile "bars" / "stack" thumbnail styles)
    const bars = section ? Array.from(section.querySelectorAll('[data-mm-gallery-bars] > span')) : [];
    const countEl = section ? section.querySelector('[data-mm-gallery-count]') : null;
    let loadedThrough = 4;

    const thumbsContainer = thumbs[0] ? thumbs[0].parentElement : null;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function scrollThumbIntoView(idx) {
      const thumb = thumbs[idx];
      if (!thumb || !thumbsContainer) return;
      const cw = thumbsContainer.clientWidth;
      const ch = thumbsContainer.clientHeight;
      const sw = thumbsContainer.scrollWidth;
      const sh = thumbsContainer.scrollHeight;
      const opts = { behavior: reduceMotion ? 'auto' : 'smooth' };
      let needs = false;
      if (sw > cw + 1) {
        const target = thumb.offsetLeft - (cw - thumb.offsetWidth) / 2;
        opts.left = Math.max(0, Math.min(target, sw - cw));
        needs = true;
      }
      if (sh > ch + 1) {
        const target = thumb.offsetTop - (ch - thumb.offsetHeight) / 2;
        opts.top = Math.max(0, Math.min(target, sh - ch));
        needs = true;
      }
      if (needs) thumbsContainer.scrollTo(opts);
    }

    function ensureLoadedTo(idx) {
      const target = Math.min(idx + 4, last);
      if (target <= loadedThrough) return;
      for (let i = loadedThrough + 1; i <= target; i++) {
        const img = slides[i] && slides[i].querySelector('img[data-mm-deferred]');
        if (!img) continue;
        const realSrc = img.getAttribute('data-src');
        const realSrcset = img.getAttribute('data-srcset');
        if (realSrcset) img.setAttribute('srcset', realSrcset);
        if (realSrc) img.setAttribute('src', realSrc);
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-mm-deferred');
      }
      loadedThrough = target;
    }

    function setArrow(btn, disabled) {
      if (!btn) return;
      btn.disabled = disabled;
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    // Slide position is a spring (mm-spring.js) in px: arrows, dots and thumbs
    // retarget it, a swipe grabs it mid-flight and hands it the flick velocity.
    // Without MMSpring the CSS transition on the track does the move.
    const spring = (track && window.MMSpring) ? MMSpring.create({
      stiffness: MMSpring.PRESETS.slider.stiffness,
      damping: MMSpring.PRESETS.slider.damping,
      precision: 0.5,
      onUpdate: function(x) { track.style.transform = 'translate3d(' + x + 'px,0,0)'; },
      onRest: function() { settleLoop(); }
    }) : null;
    if (spring) track.style.transition = 'none';

    // Infinite scroll (section setting → data-mm-gallery-loop; needs the
    // spring). A copy of the last slide sits before the first and a copy of
    // the first after the last. Moving onto a copy and then silently jumping
    // to the real slide it duplicates makes last → first one seamless step.
    const loop = !!(spring && last > 0 && gallery.hasAttribute('data-mm-gallery-loop'));
    const offset = loop ? 1 : 0;   // track positions taken by the leading copy
    if (loop) {
      const copy = function(slide) {
        const c = slide.cloneNode(true);
        c.removeAttribute('id');
        c.classList.remove('is-active');
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('data-mm-clone', '');
        c.querySelectorAll('[id]').forEach(function(el) { el.removeAttribute('id'); });
        c.querySelectorAll('button, a').forEach(function(el) { el.setAttribute('tabindex', '-1'); });
        c.querySelectorAll('img[data-mm-deferred]').forEach(function(img) {
          if (img.getAttribute('data-srcset')) img.setAttribute('srcset', img.getAttribute('data-srcset'));
          if (img.getAttribute('data-src')) img.setAttribute('src', img.getAttribute('data-src'));
        });
        return c;
      };
      track.insertBefore(copy(slides[last]), track.firstChild);
      track.appendChild(copy(slides[0]));
    }

    // One step = a slide plus the track gap (slides are narrower than the
    // frame in the mobile "peek" thumbnail style).
    function slideW() {
      const s = slides[0];
      if (!s || !track) return gallery.clientWidth || 1;
      return (s.offsetWidth + (parseFloat(getComputedStyle(track).columnGap) || 0)) || gallery.clientWidth || 1;
    }
    function posOf(i) { return -(i + offset) * slideW(); }
    function place(vel, visual) {
      if (spring) spring.goTo(posOf(visual === undefined ? current : visual), vel);
      else if (track) track.style.transform = restTransform();
    }
    // Loop: while showing a copy, relocate (position + target, velocity kept)
    // onto the real slide it duplicates — invisible, the two are identical.
    function wrapPos() {
      if (!loop) return;
      const w = slideW(), n = slides.length, x = spring.value();
      if (x > -w * 0.5) spring.shift(-n * w);
      else if (x < -(n + 0.5) * w) spring.shift(n * w);
    }
    function settleLoop() {
      if (!loop) return;
      const w = slideW(), n = slides.length, x = spring.value();
      if (x > -w * 0.5 || x < -(n + 0.5) * w) spring.set(posOf(current));
    }
    window.addEventListener('resize', function() {
      if (spring && !spring.moving()) spring.set(posOf(current));
    });
    if (loop) spring.set(posOf(0));

    function goTo(idx, vel) {
      let visual = idx;
      if (loop) {
        wrapPos();
        if (idx < 0) { idx = last; visual = -1; }
        else if (idx > last) { idx = 0; visual = last + 1; }
      } else {
        if (idx < 0) idx = 0;
        if (idx > last) idx = last;
        visual = idx;
      }
      if (idx === current) return;

      ensureLoadedTo(idx);

      slides[current].classList.remove('is-active');
      slides[current].setAttribute('aria-hidden', 'true');
      slides[idx].classList.add('is-active');
      slides[idx].setAttribute('aria-hidden', 'false');

      if (thumbs[current]) thumbs[current].setAttribute('aria-selected', 'false');
      if (thumbs[idx])     thumbs[idx].setAttribute('aria-selected', 'true');

      scrollThumbIntoView(idx);

      if (galleryDots.length) {
        galleryDots[current].classList.remove('is-active');
        galleryDots[idx].classList.add('is-active');
      }
      bars.forEach(function(b, i) {
        b.classList.toggle('is-active', i === idx);
        b.classList.toggle('is-past', i < idx);
      });
      if (countEl) countEl.textContent = (idx + 1) + ' / ' + slides.length;

      current = idx;
      place(vel, visual);
      setArrow(prevBtn, !loop && current <= 0);
      setArrow(nextBtn, !loop && current >= last);
    }

    thumbs.forEach(function(btn, i) {
      btn.addEventListener('click', function() { goTo(i); });
    });
    if (prevBtn) prevBtn.addEventListener('click', function() { goTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function() { goTo(current + 1); });

    setArrow(prevBtn, !loop);
    setArrow(nextBtn, last <= 0);

    // Touch swipe — the strip follows the finger, then commits on distance
    // or a quick flick (clamps at the ends). The axis locks on the first
    // clear move, so a vertical page scroll that starts on the photo never
    // flips the image. touch-action hands vertical pans and pinch-zoom to
    // the browser and leaves horizontal ones to us, which keeps every
    // listener passive (no scroll jank over the hero).
    gallery.style.touchAction = 'pan-y pinch-zoom';
    let sx = null, sy = 0, axis = '', lastX = 0, lastT = 0, vx = 0, grabX = 0;

    function restTransform() {
      return 'translateX(' + (-current * slideW()) + 'px)';
    }

    // Spring path. Past the first/last image the strip rubber-bands on the
    // hyperbola d·x/(x+d) (d = viewport width): ~1:1 at first, then less and
    // less, never more than one screen. With infinite scroll the copies at
    // each end are free travel, so the band only starts beyond them.
    // Raw (unbanded) finger position:
    function rawPos(dx) { return grabX + dx; }
    function minPos() { return loop ? -(last + 2) * slideW() : -last * slideW(); }
    function banded(raw) {
      const min = minPos(), d = window.innerWidth || slideW();
      if (raw > 0) return MMSpring.rubber(raw, d);
      if (raw < min) return min - MMSpring.rubber(min - raw, d);
      return raw;
    }
    function springRelease(endX) {
      const w = slideW();
      const raw = rawPos(endX - sx);
      const v = vx * 1000;                          // px/ms → px/s for the spring
      const f = -raw / w - offset;                  // fractional slide under the finger
      const d = f - current;
      let idx = current;
      if (Math.abs(v) > 400) idx = v < 0 ? Math.floor(f) + 1 : Math.ceil(f) - 1;
      else if (Math.abs(d) * w > Math.min(w * 0.2, 80)) idx = d > 0 ? Math.max(current + 1, Math.round(f)) : Math.min(current - 1, Math.round(f));
      idx = loop ? Math.max(-1, Math.min(last + 1, idx)) : Math.max(0, Math.min(last, idx));
      if (idx !== current) goTo(idx, v);
      else place(v);                                // spring back with the flick velocity
    }

    function endSwipe(commitAllowed, endX) {
      if (axis === 'x' && spring) {
        if (commitAllowed) springRelease(endX);
        else place(0);
      } else if (axis === 'x' && track) {
        const dx = endX - sx;
        const w = gallery.clientWidth || 1;
        const far = Math.abs(dx) > Math.min(w * 0.2, 80);
        const flick = Math.abs(vx) > 0.4 && Math.abs(dx) > 20;
        track.style.transition = '';
        const idx = commitAllowed && (far || flick) ? (dx < 0 ? current + 1 : current - 1) : current;
        const clamped = Math.max(0, Math.min(last, idx));
        if (clamped !== current) goTo(clamped);
        else track.style.transform = restTransform();   // spring back
      } else if (spring) {
        place();   // a tap or a vertical scroll caught it mid-flight: let it finish
      }
      sx = null; axis = '';
    }

    gallery.addEventListener('touchstart', function(e) {
      if (e.touches.length !== 1) { endSwipe(false, 0); return; }   // pinch, not a swipe
      sx = e.touches[0].clientX; sy = e.touches[0].clientY;
      axis = ''; vx = 0; lastX = sx; lastT = e.timeStamp;
      if (spring) {
        // Catch it mid-flight: freeze where it is and drag from there (unbanded)
        spring.stop();
        wrapPos();
        const x = spring.value(), min = minPos(), d = window.innerWidth || slideW();
        grabX = x > 0 ? MMSpring.unrubber(x, d) : x < min ? min - MMSpring.unrubber(min - x, d) : x;
      }
    }, { passive: true });

    gallery.addEventListener('touchmove', function(e) {
      if (sx === null) return;
      if (e.touches.length !== 1) { endSwipe(false, e.touches[0].clientX); return; }
      const x = e.touches[0].clientX, y = e.touches[0].clientY;
      const dx = x - sx, dy = y - sy;
      if (!axis) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        axis = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y';
        if (axis === 'x' && track) track.style.transition = 'none';
      }
      if (axis !== 'x' || !track) return;
      const dt = e.timeStamp - lastT;
      if (dt > 0) vx = 0.6 * ((x - lastX) / dt) + 0.4 * vx;
      lastX = x; lastT = e.timeStamp;
      if (spring) { spring.set(banded(rawPos(dx))); return; }
      // Past the first/last image the strip resists instead of sliding free
      const resist = (dx > 0 && current <= 0) || (dx < 0 && current >= last);
      track.style.transform = 'translateX(calc(' + (-current * 100) + '% + ' + (resist ? dx * 0.3 : dx) + 'px))';
    }, { passive: true });

    gallery.addEventListener('touchend', function(e) {
      if (sx === null) return;
      endSwipe(true, e.changedTouches[0].clientX);
    }, { passive: true });
    gallery.addEventListener('touchcancel', function() {
      if (sx === null) return;
      endSwipe(false, lastX);
    }, { passive: true });
  }

  // ── Mini review carousel ──────────────────────────────────────────────
  const reviewWrap = document.querySelector('[data-mm-mini-reviews]');
  if (reviewWrap && !reviewWrap.__mmMiniBound) {
    reviewWrap.__mmMiniBound = true;
    const reviews = Array.from(reviewWrap.querySelectorAll('.mm-buybox__review'));
    const dotsWrap = reviewWrap.querySelector('.mm-buybox__review-dots');
    let rCurrent = 0;

    // Build dots
    if (dotsWrap && reviews.length > 1) {
      reviews.forEach(function(_, i) {
        var d = document.createElement('span');
        d.className = 'mm-buybox__review-dot' + (i === 0 ? ' mm-buybox__review-dot--active' : '');
        dotsWrap.appendChild(d);
      });
    }

    function goToReview(idx) {
      if (idx < 0) idx = reviews.length - 1;
      if (idx >= reviews.length) idx = 0;
      reviews[rCurrent].hidden = true;
      var dots = dotsWrap ? Array.from(dotsWrap.querySelectorAll('.mm-buybox__review-dot')) : [];
      if (dots[rCurrent]) dots[rCurrent].classList.remove('mm-buybox__review-dot--active');
      rCurrent = idx;
      reviews[rCurrent].hidden = false;
      if (dots[rCurrent]) dots[rCurrent].classList.add('mm-buybox__review-dot--active');
    }

    var prevR = reviewWrap.querySelector('[data-mm-mini-review-prev]');
    var nextR = reviewWrap.querySelector('[data-mm-mini-review-next]');
    if (prevR) prevR.addEventListener('click', function(e) { e.preventDefault(); goToReview(rCurrent - 1); });
    if (nextR) nextR.addEventListener('click', function(e) { e.preventDefault(); goToReview(rCurrent + 1); });
  }
};
window.mmHeroInit();

/* mmGallerySet(items) — replace the gallery with [{src, alt}] images (offer-driven, assets/mm-offer-gallery.js);
   mmGallerySet(null) restores the original slides. Rebuilds from the pristine markup and swaps the whole
   #mm-gallery node, so mmHeroInit rebinds on a fresh node (no stacked listeners). */
window.mmGallerySet = function(items) {
  const root = document.getElementById('mm-gallery');
  if (!root || !window.__mmGalHTML) return;
  const esc = function(s) { return String(s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;'); };
  const sized = function(src, w) {
    try { const u = new URL(src, location.origin); u.searchParams.set('width', w); return u.href; } catch (e) { return src; }
  };
  const tpl = document.createElement('template');
  tpl.innerHTML = window.__mmGalHTML;
  const fresh = tpl.content.firstElementChild;
  const skel = fresh.querySelector('[data-mm-skel-media]');
  if (skel) skel.remove();
  if (items && items.length) {
    const track = fresh.querySelector('[data-mm-gallery-track]');
    track.innerHTML = items.map(function(it, i) {
      return '<div class="mm-hero__slide' + (i ? '' : ' is-active') + '" id="mm-slide-' + i + '" data-slide="' + i +
        '" data-media-type="image" role="tabpanel" aria-hidden="' + (i ? 'true' : 'false') + '" aria-label="' + esc(it.alt) + '">' +
        '<img src="' + esc(sized(it.src, 960)) + '" srcset="' + esc(sized(it.src, 540)) + ' 540w, ' + esc(sized(it.src, 960)) + ' 960w, ' +
        esc(sized(it.src, 1280)) + ' 1280w" sizes="(min-width: 980px) 600px, calc(100vw - 12px)" alt="' + esc(it.alt) +
        '" loading="' + (i < 3 ? 'eager' : 'lazy') + '" decoding="async"></div>';
    }).join('');
    const thumbs = fresh.querySelector('.mm-hero__thumbs');
    if (thumbs) {
      if (items.length > 1) {
        thumbs.innerHTML = items.map(function(it, i) {
          return '<button class="mm-hero__thumb" role="tab" aria-selected="' + (i ? 'false' : 'true') + '" aria-controls="mm-slide-' + i +
            '" aria-label="' + esc(it.alt) + '" data-thumb="' + i + '"><img src="' + esc(sized(it.src, 160)) +
            '" width="84" height="84" alt="' + esc(it.alt) + '" loading="lazy" decoding="async"></button>';
        }).join('');
      } else thumbs.remove();
    }
    const bars = fresh.querySelector('[data-mm-gallery-bars]');
    if (bars) bars.innerHTML = items.map(function(_, i) { return '<span' + (i ? '' : ' class="is-active"') + '></span>'; }).join('');
    const count = fresh.querySelector('[data-mm-gallery-count]');
    if (count) count.textContent = '1 / ' + items.length;
  }
  root.replaceWith(fresh);
  window.mmHeroInit();
};
